var _excluded = ["action", "actionButtonLabel", "appearance", "avatar", "content", "date", "icon", "id", "onClickItem", "open", "read", "title", "type"];
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
import * as React from 'react';
import mergeRefs from 'react-merge-refs';
import getScrollbarSize from '../utils/get_scrollbar_size';
import ActionButton from '../shared/action_button';
import IconHelp from '../icons/icon_help';
import IconInfo from '../icons/icon_info';
import IconCheckmarkCircle from '../icons/icon_checkmark_circle';
import IconWarningAlt from '../icons/icon_warning_alt';
import IconXCircle from '../icons/icon_x_circle';
import useStyles from './use_styles';
import useFocusVisible from '../utils/use_focus_visible';
import useDirection from '../direction/use_direction';
import ContextualMenu from '../contextual_menu';
import { useTheme } from '@veneer/theme';
import { jsx as ___EmotionJSX } from "@emotion/core";
var notificationIcons = {
  help: ___EmotionJSX(IconHelp, null),
  informative: ___EmotionJSX(IconInfo, null),
  negative: ___EmotionJSX(IconXCircle, null),
  positive: ___EmotionJSX(IconCheckmarkCircle, null),
  warning: ___EmotionJSX(IconWarningAlt, null)
};
var NotificationCenterItem = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  var _action$props;
  var {
      action,
      actionButtonLabel,
      appearance = 'notification',
      avatar,
      content,
      date,
      icon,
      id,
      onClickItem,
      open: parentOpened,
      read = false,
      title,
      type
    } = _ref,
    other = _objectWithoutProperties(_ref, _excluded);
  var isContextualMenu = (action === null || action === void 0 ? void 0 : action.type) === ContextualMenu;
  var notificationIcon = icon || (type ? notificationIcons[type] : undefined);
  var isSection = appearance === 'section';
  var {
    isRTL
  } = useDirection();
  var theme = useTheme();
  var {
    handleBlurVisible,
    handleFocusVisible,
    ref: focusVisibleRef
  } = useFocusVisible();
  var [openedAction, setOpenedAction] = React.useState(false);
  var [focusedId, setFocusedId] = React.useState(undefined);
  var [mouseOver, setMouseOver] = React.useState(false);
  var actionButtonRef = React.useRef(null);
  var liRef = React.useRef();
  var handleRef = mergeRefs([ref, liRef, focusVisibleRef]);
  var openedActionMenu = openedAction && parentOpened;
  var hasActionOnClick = (action === null || action === void 0 || (_action$props = action.props) === null || _action$props === void 0 ? void 0 : _action$props.onClick) && !isContextualMenu;
  var isClickable = hasActionOnClick || onClickItem;
  var isFocused = id === focusedId && isClickable;
  var showNotification = !read && (!mouseOver || !action);
  React.useEffect(() => {
    if (!parentOpened) {
      setOpenedAction(false);
    }
  }, [parentOpened]);
  var style = useStyles({
    isClickable,
    isFocused,
    isSection,
    open: openedActionMenu,
    showNotification,
    type
  });
  React.useEffect(() => {
    var _document$getElementB;
    var parentElement = (_document$getElementB = document.getElementById(id.toString())) === null || _document$getElementB === void 0 ? void 0 : _document$getElementB.parentElement;
    if (parentElement && parentElement.scrollHeight > parentElement.clientHeight) {
      var customPadding = isRTL ? 'padding-left' : 'padding-right';
      if (openedAction) {
        var isCustomScrollbar = theme.mode !== 'contrast';
        var compensatePadding = getScrollbarSize(isCustomScrollbar);
        parentElement.style.overflowY = 'hidden';
        parentElement.style.setProperty(customPadding, "".concat(compensatePadding, "px"));
      } else {
        parentElement.style.removeProperty(customPadding);
        parentElement.style.removeProperty('overflow-y');
      }
    }
  }, [id, isRTL, openedAction, theme.mode]);
  var handleActionButtonClick = React.useCallback(() => {
    setOpenedAction(true);
  }, []);
  var handleActionClose = React.useCallback(() => {
    setOpenedAction(false);
  }, []);
  var handleBlur = React.useCallback(event => {
    setFocusedId(undefined);
    handleBlurVisible(event);
  }, [handleBlurVisible]);
  var handleFocus = React.useCallback(event => {
    if (handleFocusVisible(event) && event.target instanceof HTMLButtonElement && event.target.type !== 'button') {
      setFocusedId(id);
    }
  }, [handleFocusVisible, id]);
  var handleActionClick = React.useCallback((event, option) => {
    var _action$props$onClick, _action$props2;
    action === null || action === void 0 || (_action$props$onClick = (_action$props2 = action.props).onClick) === null || _action$props$onClick === void 0 || _action$props$onClick.call(_action$props2, event, option, id);
  }, [action === null || action === void 0 ? void 0 : action.props, id]);
  var itemContent;
  if (isSection) {
    itemContent = title;
  } else {
    itemContent = ___EmotionJSX(React.Fragment, null, ___EmotionJSX("div", {
      css: style.leftSection
    }, avatar || notificationIcon && ___EmotionJSX("span", {
      css: style.icon
    }, /*#__PURE__*/React.cloneElement(notificationIcon, {
      filled: true,
      size: 24
    }))), ___EmotionJSX("div", {
      css: style.center
    }, title, content && ___EmotionJSX("span", {
      css: style.content
    }, content), date && ___EmotionJSX("span", {
      css: style.date
    }, date)), ___EmotionJSX("div", {
      css: style.unreadCircle
    }), action && /*#__PURE__*/React.cloneElement(action, {
      onClick: handleActionClick,
      onClose: handleActionClose,
      open: openedActionMenu,
      placement: 'bottom-end',
      portal: true
    }, ___EmotionJSX(ActionButton, {
      "aria-label": actionButtonLabel,
      customStyle: style.actionButton,
      onClick: handleActionButtonClick,
      ref: actionButtonRef
    })));
  }
  var handleClick = React.useCallback(event => {
    var isEventFromItem = event.target === event.currentTarget;
    if (isEventFromItem && onClickItem) {
      onClickItem(event, undefined, id);
    } else if (hasActionOnClick && isEventFromItem) {
      action.props.onClick(event, action.props.options[0], id);
    }
  }, [action === null || action === void 0 ? void 0 : action.props, hasActionOnClick, id, onClickItem]);
  var handleKeyDown = React.useCallback(event => {
    if (event.key === 'Enter' || event.key === ' ') {
      handleClick(event);
    }
  }, [handleClick]);
  var handleMouseLeave = React.useCallback(() => setMouseOver(false), []);
  var handleMouseEnter = React.useCallback(() => setMouseOver(false), []);
  return ___EmotionJSX("li", _extends({}, other, {
    role: "menuitem",
    onClick: handleClick,
    onKeyDown: handleKeyDown,
    onFocus: handleFocus,
    onBlur: handleBlur,
    tabIndex: isClickable ? 0 : -1,
    onMouseLeave: handleMouseLeave,
    onMouseOver: handleMouseEnter,
    id: id.toString(),
    css: style.li,
    ref: handleRef
  }), itemContent);
});
NotificationCenterItem.displayName = 'NotificationCenterItem';
export default NotificationCenterItem;